import React, { useRef, memo } from "react";
import { useEditor, isImageAsset } from "./store";
import { secondsToPx } from "./helpers";
import { gainToY, gainToNormalized, normalizedToGain, GAIN_PADDING } from "./gainUtils";
import { WaveformCanvas, GainFadeCanvas } from "./AudioVisualization";
import { cn } from "@/lib/utils/shadcn";
import SafeImage from "@/components/ui/SafeImage";
import { FaLock } from "react-icons/fa";

// ===== Fade Handle (left = fadeIn, right = fadeOut) =====
function FadeHandle({ clipId, clipDuration, value, side, clipWidth, clipHeight, gain }) {
  const ref = useRef(null);
  const handleSize = 10;
  const isLeft = side === "left";
  const fadePx = clipDuration > 0 ? (value / clipDuration) * clipWidth : 0;
  const xPos = isLeft ? fadePx : clipWidth - fadePx;
  const yPos = gainToY(gain, clipHeight);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const clipEl = ref.current.parentElement;
    useEditor.getState().beginHistoryTransaction("fade");
    let rafId = null;
    let pendingUpdate = null;
    const field = isLeft ? "fadeIn" : "fadeOut";

    const flushUpdate = () => {
      rafId = null;
      if (pendingUpdate !== null) {
        useEditor.getState().updateClip(clipId, { [field]: pendingUpdate });
        pendingUpdate = null;
      }
    };

    const onPointerMove = (ev) => {
      const clipRect = clipEl.getBoundingClientRect();
      const localX = ev.clientX - clipRect.left;
      let newValue;
      if (isLeft) {
        newValue = (localX / clipWidth) * clipDuration;
      } else {
        newValue = ((clipWidth - localX) / clipWidth) * clipDuration;
      }
      pendingUpdate = Math.max(0, Math.min(clipDuration, Math.round(newValue * 1000) / 1000));
      if (rafId === null) {
        rafId = requestAnimationFrame(flushUpdate);
      }
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (pendingUpdate !== null) {
        useEditor.getState().updateClip(clipId, { [field]: pendingUpdate });
      }
      useEditor.getState().commitHistoryTransaction();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const clampedLeft = Math.max(0, Math.min(clipWidth - handleSize, xPos - handleSize / 2));

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onDoubleClick={(e) => {
        e.stopPropagation();
        useEditor.getState().updateClip(clipId, { [isLeft ? "fadeIn" : "fadeOut"]: 0 });
      }}
      className="absolute opacity-0 group-hover:opacity-100 z-20 cursor-ew-resize"
      style={{
        width: handleSize,
        height: handleSize,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.9)",
        border: "1.5px solid rgba(100,100,255,0.7)",
        left: clampedLeft,
        top: Math.max(0, Math.min(clipHeight - handleSize, yPos - handleSize / 2)),
        boxShadow: "0 0 4px rgba(100,100,255,0.5)",
      }}
      title={isLeft ? `Fade In: ${value.toFixed(2)}s` : `Fade Out: ${value.toFixed(2)}s`}
    />
  );
}

// ===== Gain Handle (center, vertical drag) =====
function GainHandle({ clipId, gain, clipWidth, clipHeight, fadeIn, fadeOut, clipDuration }) {
  const ref = useRef(null);
  const handleW = 16;
  const handleH = 6;
  const fadeInPx = clipDuration > 0 ? (fadeIn / clipDuration) * clipWidth : 0;
  const fadeOutPx = clipDuration > 0 ? (fadeOut / clipDuration) * clipWidth : 0;
  const leftBound = fadeInPx;
  const rightBound = clipWidth - fadeOutPx;
  const centerX = (leftBound + rightBound) / 2;
  const yPos = gainToY(gain, clipHeight);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startGain = gain;
    useEditor.getState().beginHistoryTransaction("gain");
    let rafId = null;
    let pendingGain = null;

    const flushGain = () => {
      rafId = null;
      if (pendingGain !== null) {
        useEditor.getState().updateClip(clipId, { gain: pendingGain });
        pendingGain = null;
      }
    };

    const onPointerMove = (ev) => {
      const dy = ev.clientY - startY;
      const startNorm = gainToNormalized(startGain);
      const drawHeight = clipHeight - GAIN_PADDING * 2;
      const newNorm = Math.max(0, Math.min(1, startNorm - dy / drawHeight));
      pendingGain = Math.round(normalizedToGain(newNorm) * 100) / 100;
      if (rafId === null) {
        rafId = requestAnimationFrame(flushGain);
      }
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (pendingGain !== null) {
        useEditor.getState().updateClip(clipId, { gain: pendingGain });
      }
      useEditor.getState().commitHistoryTransaction();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const clampedLeft = Math.max(0, Math.min(clipWidth - handleW, centerX - handleW / 2));
  const clampedTop = Math.max(0, Math.min(clipHeight - handleH, yPos - handleH / 2));

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onDoubleClick={(e) => {
        e.stopPropagation();
        useEditor.getState().updateClip(clipId, { gain: 1 });
      }}
      className="absolute opacity-0 group-hover:opacity-100 z-20 cursor-ns-resize"
      style={{
        width: handleW,
        height: handleH,
        borderRadius: 3,
        background: "rgba(255,255,255,0.9)",
        border: "1.5px solid rgba(100,100,255,0.7)",
        left: clampedLeft,
        top: clampedTop,
        boxShadow: "0 0 4px rgba(100,100,255,0.5)",
      }}
      title={`Gain: ${gain.toFixed(2)}`}
    />
  );
}

// Custom comparison for TimelineClip to prevent unnecessary re-renders
// Only compare values that affect visual rendering - ignore callback references
function areClipPropsEqual(prevProps, nextProps) {
  // Check clip data that affects rendering
  if (prevProps.clip.id !== nextProps.clip.id) return false;
  if (prevProps.clip.start !== nextProps.clip.start) return false;
  if (prevProps.clip.duration !== nextProps.clip.duration) return false;
  if (prevProps.clip.in !== nextProps.clip.in) return false;
  if (prevProps.clip.trackId !== nextProps.clip.trackId) return false;
  if (prevProps.clip.assetId !== nextProps.clip.assetId) return false;
  if (prevProps.clip.gain !== nextProps.clip.gain) return false;
  if (prevProps.clip.fadeIn !== nextProps.clip.fadeIn) return false;
  if (prevProps.clip.fadeOut !== nextProps.clip.fadeOut) return false;
  if (prevProps.clip.allowTrimming !== nextProps.clip.allowTrimming) return false;
  if (prevProps.clip.allowSplit !== nextProps.clip.allowSplit) return false;
  if (prevProps.clip.sketchId !== nextProps.clip.sketchId) return false;
  
  // Check asset data that affects rendering
  if (prevProps.asset?.id !== nextProps.asset?.id) return false;
  if (prevProps.asset?.thumbnailUrl !== nextProps.asset?.thumbnailUrl) return false;
  if (prevProps.asset?.url !== nextProps.asset?.url) return false;
  if (prevProps.asset?.type !== nextProps.asset?.type) return false;
  if (prevProps.asset?.name !== nextProps.asset?.name) return false;
  if (prevProps.asset?.duration !== nextProps.asset?.duration) return false;
  
  // Check other props that affect visual appearance
  if (prevProps.trackType !== nextProps.trackType) return false;
  if (prevProps.trackIndex !== nextProps.trackIndex) return false;
  if (prevProps.rowH !== nextProps.rowH) return false;
  if (prevProps.zoom !== nextProps.zoom) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isDragging !== nextProps.isDragging) return false;
  if (prevProps.isTrimming !== nextProps.isTrimming) return false;
  if (prevProps.trimSide !== nextProps.trimSide) return false;
  if (prevProps.isCutMode !== nextProps.isCutMode) return false;
  if (prevProps.isTrimmingDisabled !== nextProps.isTrimmingDisabled) return false;
  if (prevProps.isAssetDragHovered !== nextProps.isAssetDragHovered) return false;
  
  // Note: We intentionally do NOT compare callbacks here.
  // Callbacks don't affect visual rendering, and they may change references
  // due to useCallback dependencies. The component will still receive
  // the latest callbacks when user interacts.
  
  return true;
}

// Memoized clip component to prevent re-renders during playback
const TimelineClip = memo(function TimelineClip({
  clip,
  asset,
  trackType,
  trackIndex,
  rowH,
  zoom,
  isSelected,
  isDragging,
  isTrimming,
  trimSide,
  isCutMode,
  isTrimmingDisabled,
  isAssetDragHovered,
  onClipPointerDown,
  onTrimLeftPointerDown,
  onTrimRightPointerDown,
  onClipDoubleClick,
}) {
  const left = secondsToPx(clip.start, zoom);
  const top = trackIndex * rowH + 8;
  const w = Math.max(20, secondsToPx(clip.duration, zoom));
  // For empty clips (asset is null), determine type from track
  const isVideo = asset?.type === "video" || (!asset && trackType === "video");
  const isImage = isImageAsset(asset);
  const isAudio = asset?.type === "audio" || (!asset && trackType === "audio");
  const isScript = !!asset?.scriptId;
  const isVideoLike = isVideo || isImage;
  const isSplittingDisabled = false;
  const clipThumbnailSrc = asset?.thumbnailUrl || asset?.url || null;

  // Pass clip to parent handlers - no useCallback needed since our custom memo 
  // comparison ignores callback changes, and we need fresh clip data
  const handlePointerDown = (e) => onClipPointerDown(e, clip);
  const handleTrimLeftPointerDown = (e) => onTrimLeftPointerDown(e, clip);
  const handleTrimRightPointerDown = (e) => onTrimRightPointerDown(e, clip);
  const handleDoubleClick = (e) => {
    if (asset?.scriptId || isImage || (!asset && trackType === "video")) {
      e.stopPropagation();
      onClipDoubleClick?.(clip, asset);
    }
  };

  return (
    <div
      data-clip
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
      className={cn(
        "absolute rounded border select-none group overflow-hidden",
        isScript ? "bg-amber-800/40 border-amber-400/40" : isVideoLike ? "bg-purple-800/60 border-purple-400/40" : "bg-emerald-800/30 border-emerald-400/40",
        isCutMode ? (isSplittingDisabled ? "cursor-not-allowed" : "cursor-crosshair") : (isDragging ? "cursor-grabbing" : "cursor-grab"),
        isTrimming && "ring-2 ring-white/50",
        isSelected && (isScript ? "bg-amber-700/80 border-amber-400 ring-2 ring-amber-400" : isVideoLike ? "bg-purple-700/80 border-purple-400 ring-2 ring-purple-400" : "bg-emerald-700/80 border-emerald-400 ring-2 ring-emerald-400"),
        isAssetDragHovered && "ring-2 ring-primary brightness-125"
      )}
      style={{ left, top, width: w, height: 54 }}
    >
      {/* Waveform for audio clips only */}
      {isAudio && asset?.url && (
        <WaveformCanvas
          url={asset.url}
          width={w}
          height={54}
          inPoint={clip.in}
          duration={clip.duration}
          assetDuration={asset.duration}
          gain={clip.gain ?? 1}
          fadeIn={clip.fadeIn ?? 0}
          fadeOut={clip.fadeOut ?? 0}
          clipStart={clip.start}
          color={isScript ? "rgba(217, 119, 6, 0.7)" : isAudio ? "rgba(16, 185, 129, 0.7)" : "rgba(147, 51, 234, 0.7)"}
        />
      )}
      
      {/* Gain/Fade visualization line for all non-image clips */}
      {!isImage && asset && (
        <GainFadeCanvas
          clip={clip}
          width={w}
          height={54}
        />
      )}

      {/* Fade In handle */}
      {!isImage && !isCutMode && asset && (
        <FadeHandle
          clipId={clip.id}
          clipDuration={clip.duration}
          value={clip.fadeIn ?? 0}
          side="left"
          clipWidth={w}
          clipHeight={54}
          gain={clip.gain ?? 1}
        />
      )}

      {/* Fade Out handle */}
      {!isImage && !isCutMode && asset && (
        <FadeHandle
          clipId={clip.id}
          clipDuration={clip.duration}
          value={clip.fadeOut ?? 0}
          side="right"
          clipWidth={w}
          clipHeight={54}
          gain={clip.gain ?? 1}
        />
      )}

      {/* Gain handle */}
      {!isImage && !isCutMode && asset && (
        <GainHandle
          clipId={clip.id}
          gain={clip.gain ?? 1}
          clipWidth={w}
          clipHeight={54}
          fadeIn={clip.fadeIn ?? 0}
          fadeOut={clip.fadeOut ?? 0}
          clipDuration={clip.duration}
        />
      )}

      {/* Left trim handle - hidden in cut mode or when trimming is disabled */}
      {!isCutMode && !isTrimmingDisabled && (
        <div
          onPointerDown={handleTrimLeftPointerDown}
          className={cn(
            "absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-10",
            "opacity-0 group-hover:opacity-100",
            isScript ? "bg-amber-300/80 hover:bg-amber-200" : isVideoLike ? "bg-purple-300/80 hover:bg-purple-200" : "bg-emerald-300/80 hover:bg-emerald-200",
            "rounded-l",
            isTrimming && trimSide === "left" ? "opacity-100 !bg-white" : "",
          )}
          title="Drag to trim start"
        />
      )}
      
      {/* Clip content */}
      <div className="flex h-full relative z-[1] items-start">
        {/* Thumbnail (only for video/image clips) */}
        {isVideoLike && clipThumbnailSrc && (
          <div className="h-full aspect-video flex-shrink-0 overflow-hidden relative">
            <SafeImage 
              key={clipThumbnailSrc}
              src={clipThumbnailSrc}
              alt="" 
              fill
              className="object-cover h-full"
              draggable={false}
            />
          </div>
        )}
        {!isVideoLike && (
          <div className="flex items-center gap-1 px-2 py-1 bg-black/70 rounded-br-sm max-w-3/4 group-hover:hidden">
            {!clip.allowTrimming && !isScript && <FaLock className="w-2 h-2 text-orange-500 flex-shrink-0" />}
              <span className="text-[10px] text-secondary-foreground font-mono truncate">
                {asset ? (asset.name || asset.url.split("/").pop()) : "Empty Audio"}
              </span>
          </div>
        )}
      </div>
      

      {/* Right trim handle - hidden in cut mode or when trimming is disabled */}
      {!isCutMode && !isTrimmingDisabled && (
        <div
          onPointerDown={handleTrimRightPointerDown}
          className={cn(
            "absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-10",
            "opacity-0 group-hover:opacity-100",
            isScript ? "bg-amber-300/80 hover:bg-amber-200" : isVideoLike ? "bg-purple-300/80 hover:bg-purple-200" : "bg-emerald-300/80 hover:bg-emerald-200",
            "rounded-r",
            isTrimming && trimSide === "right" ? "opacity-100 !bg-white" : "",
          )}
          title="Drag to trim end"
        />
      )}
    </div>
  );
}, areClipPropsEqual);

export default TimelineClip;
